import React from 'react';
import { useFeatures } from '../../../context/FeatureContext';
import { Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { formatMicrocreditsForDisplay, creditDecimalToMicrocredits, creditPercentage, formatMicrocredits, hasMicrocredits, sumMicrocredits } from '../../../utils/creditUnits';

/**
 * AiCreditsIndicator
 * Displays the current AI credits balance with a color-coded status indicator.
 * Shows tooltip with detailed credit information on hover.
 */
const AiCreditsIndicator = ({ className = '' }) => {
    const { entitlements } = useFeatures();

    if (!entitlements?.aiCredits || entitlements?.planSelectionRequired || entitlements?.billingAccess?.code === 'BILLING_PLAN_SELECTION_REQUIRED') {
        return null;
    }

    const { periodAllocation } = entitlements.aiCredits;
    const balanceMicrocredits = entitlements.aiCredits.balanceMicrocredits || creditDecimalToMicrocredits(entitlements.aiCredits.balance);
    const allocationMicrocredits = entitlements.aiCredits.periodAllocationMicrocredits || creditDecimalToMicrocredits(String(periodAllocation || 0));
    const percentage = creditPercentage(balanceMicrocredits, allocationMicrocredits);

    // Determine badge color based on remaining percentage
    let variant = 'success';
    if (percentage <= 10) {
        variant = 'danger';
    } else if (percentage <= 25) {
        variant = 'warning';
    } else if (percentage <= 50) {
        variant = 'info';
    }

    const tooltipContent = (
        <Tooltip id="ai-credits-tooltip">
            <div style={{ textAlign: 'left' }}>
                <strong>LedgerLoop-krediter</strong>
                <div>Tillgängliga: {formatMicrocreditsForDisplay(balanceMicrocredits)} av {formatMicrocreditsForDisplay(allocationMicrocredits)}</div>
                {hasMicrocredits(entitlements.aiCredits.reservedMicrocredits) && (
                    <div>Reserverade: {formatMicrocredits(entitlements.aiCredits.reservedMicrocredits)}. Oanvända krediter blir tillgängliga igen när kostnaden är beräknad.</div>
                )}
                {hasMicrocredits(entitlements.aiCredits.purchasedMicrocredits) && <div>Varav k&ouml;pta: {formatMicrocredits(entitlements.aiCredits.purchasedMicrocredits)}</div>}
                <div>Fr&aring;n plan: {formatMicrocredits(entitlements.aiCredits.fromPlanMicrocredits)}</div>
            </div>
        </Tooltip>
    );

    return (
        <OverlayTrigger
            placement="bottom"
            overlay={tooltipContent}
        >
            <Badge
                bg={variant}
                className={`ai-credits-badge ${className}`}
                style={{ cursor: 'pointer' }}
            >
                <i className="fas fa-robot me-1"></i>
                {formatMicrocredits(balanceMicrocredits)}
            </Badge>
        </OverlayTrigger>
    );
};

/**
 * AiCreditsBar
 * Displays a progress bar showing AI credits usage.
 */
export const AiCreditsBar = ({ className = '', showLabel = true }) => {
    const { entitlements } = useFeatures();

    if (entitlements?.planSelectionRequired || entitlements?.billingAccess?.code === 'BILLING_PLAN_SELECTION_REQUIRED') {
        return <div className={`ai-credits-bar ${className}`}><small>Välj plan för att aktivera AI-krediter</small></div>;
    }

    if (!entitlements?.aiCredits) {
        return null;
    }

    const { balance, periodAllocation } = entitlements.aiCredits;
    const balanceMicrocredits = entitlements.aiCredits.balanceMicrocredits || creditDecimalToMicrocredits(balance);
    const allocationMicrocredits = entitlements.aiCredits.periodAllocationMicrocredits || creditDecimalToMicrocredits(String(periodAllocation || 0));
    const reservedMicrocredits = entitlements.aiCredits.reservedMicrocredits || '0';
    const totalMicrocredits = sumMicrocredits([balanceMicrocredits, reservedMicrocredits]);
    // Purchases can exceed the included monthly allowance. Expand the scale so
    // reservations remain visible instead of disappearing behind a full bar.
    const scale = !hasMicrocredits(allocationMicrocredits) || creditPercentage(totalMicrocredits, allocationMicrocredits) > 100
        ? totalMicrocredits : allocationMicrocredits;
    const percentage = creditPercentage(balanceMicrocredits, scale);
    const reservedPercentage = creditPercentage(reservedMicrocredits, scale);

    return (
        <div className={`ai-credits-bar ${className}`}>
            {showLabel && (
                <div className="d-flex justify-content-between mb-1">
                    <small>LedgerLoop-krediter</small>
                    <small>{formatMicrocreditsForDisplay(balanceMicrocredits)} / {formatMicrocreditsForDisplay(allocationMicrocredits)}</small>
                </div>
            )}
            <div className="progress" style={{ height: '8px' }}>
                <div
                    className="progress-bar bg-success"
                    role="progressbar"
                    aria-label="Tillgängliga AI-krediter"
                    style={{ width: `${percentage}%` }}
                    aria-valuenow={percentage}
                    aria-valuemin="0"
                    aria-valuemax="100"
                />
                {hasMicrocredits(reservedMicrocredits) && <div
                    className="progress-bar bg-warning progress-bar-striped"
                    role="progressbar"
                    aria-label="Reserverade AI-krediter"
                    style={{ width: `${reservedPercentage}%`, border: '1px dashed #805400', boxSizing: 'border-box' }}
                    aria-valuenow={reservedPercentage}
                    aria-valuemin="0"
                    aria-valuemax="100"
                />}
            </div>
            {showLabel && <div className="d-flex flex-wrap gap-2 mt-1" style={{ fontSize: '0.7rem' }}>
                <span><span className="text-success" aria-hidden="true">●</span> Tillgängliga: {formatMicrocreditsForDisplay(balanceMicrocredits)}</span>
                <span title="Tillfälligt reserverade för AI-arbete. Oanvända krediter blir tillgängliga igen när kostnaden är beräknad.">
                    <span className="text-warning" aria-hidden="true">●</span> Reserverade: {formatMicrocreditsForDisplay(reservedMicrocredits)}
                </span>
            </div>}
        </div>
    );
};

/**
 * useAiCredits
 * Hook to access AI credits data directly
 */
export const useAiCredits = () => {
    const { entitlements } = useFeatures();

    if (!entitlements?.aiCredits || entitlements?.planSelectionRequired || entitlements?.billingAccess?.code === 'BILLING_PLAN_SELECTION_REQUIRED') {
        return {
            hasCredits: false,
            balance: 0,
            periodAllocation: 0,
            percentage: 0,
            isLow: false,
            isCritical: false
        };
    }

    const { balance, periodAllocation } = entitlements.aiCredits;
    const percentage = periodAllocation > 0 ? (balance / periodAllocation) * 100 : 0;

    return {
        hasCredits: balance > 0,
        balance,
        periodAllocation,
        percentage,
        isLow: percentage <= 25,
        isCritical: percentage <= 10,
        ...entitlements.aiCredits
    };
};

export default AiCreditsIndicator;
